import type { IAccount } from "./accounts";
import type { IAddress } from "./addresses";
import type { IAffiliatePendingCommission } from "./affiliate";
import type { IBasket, IBasketProduct } from "./baskets";
import type { IBrand } from "./brands";
import type { ICustomField } from "./brands";
import type { IClient } from "./clients";
import type { ICompany } from "./companies";
import type { ICurrency } from "./constants";
import type { IContract } from "./contracts";
import type { IGateway } from "./gateways";
import type { IImport } from "./imports";
import type { IMetaData } from "./metaData";
import type { IOrg } from "./organisation";
import type { IPayment } from "./payment";
import type { IPromotion } from "./promotions";
import type { IUser } from "./users";

export interface IInvoiceContent {
  id: string;
  created_at: string;
  invoice_id: IInvoice["id"];
  updated_at: string;
  partial_amount_credited: number;
  partial_amount_credited_converted: number;
  partial_amount_credited_formatted: string;
  partial_amount_to_credit_converted: number;
  partial_amount_to_credit_formatted: string;
  content: {
    brand: IBrand;
    client: IClient;
    client_address: IAddress;
    client_company: ICompany;
    client_email: string;
    client_fields: [];
    client_id: IClient["id"];
    custom_fields: ICustomField[];
    id: string;
    image: string;
    number: number;
    products: IInvoiceProduct[];
    promotions: IPromotion[];
  };
}

export interface IInvoiceProduct extends IBasketProduct {
  client_label: string | null;
  attributes: IInvoiceProduct[];
  options: IInvoiceProduct[];
  partial_amount_credited: number;
  partial_amount_credited_converted: number;
  partial_amount_credited_formatted: string;
  partial_amount_to_credit_converted: number;
  partial_amount_to_credit_formatted: string;
}

export interface IInvoice extends IBasket {
  affiliate_commissions?: IAffiliatePendingCommission[];
  allow_product_credit: boolean;
  cancellation_datetime?: string | null;
  current_data: IInvoiceContent;
  data: IInvoiceContent[];
  delegate_related: boolean;
  fraud_score: number;
  fraud_status: number;
  gateway?: IGateway; // available with relation
  is_consolidation: boolean;
  locked: string | null;
  net_amount_converted: number;
  next_charge_date: string;
  object_meta?: IMetaData;
  partial_amount_credited: number;
  partial_amount_credited_converted: number;
  partial_amount_credited_formatted: string;
  partial_amount_to_credit_converted: number;
  partial_amount_to_credit_formatted: string;
  payment_currency?: ICurrency; // Requires relation
  payment_currency_id: ICurrency["id"];
  payment_failed_attempts: number;
  payments: IPayment[];
  /**
   * Computed by the BE: true when this paid invoice was settled via the brand's
   * nominated offline/bank-transfer "clearing/pending arrival" method (funds not yet
   * received). Derived property — may be absent on partial invoice payloads.
   * Requires `with=last_payment_log` relation to be present on the invoice payload.
   */
  pending_payment_method?: boolean;
  product_upgrade_quantity?: number;
  products: IInvoiceProduct[];
  products_count: number; // Requires `with_count=products` relation
  proforma: boolean;
  proforma_create_datetime: string;
  proforma_number: string;
  tax_amount_converted: number;
  to_be_credited: boolean;
}

export interface ILegacyInvoice {
  account_id: IAccount["id"];
  account?: IAccount;
  brand_id: IBrand["id"];
  brand?: IBrand;
  client_id: IClient["id"];
  client?: IClient;
  contract_id: IContract["id"];
  contract?: IContract;
  content: any;
  create_datetime: string | null;
  created_at: string;
  currency: ICurrency | null;
  currency_id: ICurrency["id"] | null;
  external_id: string | null;
  id: string;
  import?: IImport;
  import_id: IImport["id"];
  invoice_id: string;
  number: string;
  org?: IOrg;
  org_id: IOrg["id"];
  staged_import: boolean;
  status_id: string | null;
  total_amount: number | null;
  total_amount_converted: number | null;
  total_amount_formatted: string | null;
  updated_at: string;
  user_id: IUser["id"] | null;
}

export enum InvoiceCategoryCode {
  NEW_CONTRACT = "new_contract",
  ADDITIONAL_SERVICE = "additional_service",
  ONE_TIME_SERVICE = "one_time_service",
  MIGRATION = "migration_pro_rata",
  RECURRENT = "recurrent",
  CREDIT_NOTE = "credit_note",
  CREDIT_NOTE_FOR_REFUND = "credit_note_for_refund",
  CONSOLIDATION = "consolidation"
}

export enum CreditRefundOptionTypes {
  CREDIT_ENTIRE_INVOICE = "credit-entire-invoice",
  CREDIT_SELECT_ITEMS = "credit-select-items",
  REFUND_INVOICE_PAYMENTS = "refund-invoice-payments"
}

export type CreditRefundOption = {
  id: CreditRefundOptionTypes;
  title: string;
};

export interface IInvoiceUnpaid {
  invoice: IInvoice;
  total: number;
}

export enum PaymentRefundOptions {
  CREDIT_NOTE_ONLY = "credit-note-only",
  ADJUSTMENT_ONLY = "adjustment-only",
  ADJUSTMENT_WITH_CREDIT_NOTE = "adjustment-with-credit-note"
}
